import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { RWTable, Table } from "common/data/table";
import type { PolyLine2 } from "geom/polyLine2Types";
import { makeBlockPerimeterFromRoomOutlines } from "../generationTools/generateBlockPerimetersTools";
import memo from "../utils/memo";

function select({ doc, blocks, rooms }: Building) {
  return {
    docRooms: doc.rooms,
    blocks,
    rooms,
  };
}

function generateBlockPerimeters(opts: ReturnType<typeof select>): {
  blockPerimeters: Table<PolyLine2[]>;
  issues: BuildingGenerationIssue[];
} {
  const issues: BuildingGenerationIssue[] = [];
  const { blocks, rooms, docRooms } = opts;

  const blockPerimeters: RWTable<PolyLine2[]> = {};

  const generatedRoomArray = Object.values(rooms);
  const docRoomArray = Object.values(docRooms);

  for (const blockId in blocks) {
    // 1. Get source rooms from doc that are active in the building.
    const sourceRoomsInBlock = docRoomArray.filter((room) => room.parentId === blockId).filter((x) => rooms[x.id]);
    const sourceOutlines = sourceRoomsInBlock.map((x) => x.outline);

    // 2. Get instance rooms from the generated building.
    // These are rooms that have an instanceId and belong to this block.
    const instanceRoomsInBlock = generatedRoomArray.filter(
      (room) => room.parentId === blockId && room.instanceId !== undefined,
    );

    const instanceOutlines = instanceRoomsInBlock.map((x) => x.outline);
    const roomOutlines = [...sourceOutlines, ...instanceOutlines];
    if (roomOutlines.length === 0) {
      // Skip empty blocks.
      continue;
    }

    const perimeters = makeBlockPerimeterFromRoomOutlines(roomOutlines);
    blockPerimeters[blockId] = perimeters;
  }

  return { blockPerimeters: blockPerimeters as Table<PolyLine2[]>, issues };
}

export default memo(select, generateBlockPerimeters);
